import React, {
  createContext,
  useContext,
  useMemo,
  useReducer,
  ReactNode,
} from 'react';

import SignIn from './SignIn';

type Action = {
  type:
    | 'SET_EMAIL'
    | 'SET_PASSWORD'
    | 'SET_STEP'
    | 'SET_COGNITO_USER'
    | 'SET_2FA_TOKEN'
    | 'SET_LOGGED'
    | 'SET_LOADING'
    | 'CLEAR';
  payload: any;
};
type Dispatch = (action: Action) => void;
type State = {
  email: string;
  password: string;
  cognitoUser: any;
  token2fa: string;
  logged: boolean;
  step:
    | 'LOGIN'
    | 'CONFIRM_EMAIL'
    | 'CONFIRM_EMAIL_CHANGE'
    | '2FA'
    | 'REQUEST_2FA_BYPASS'
    | 'BYPASS_2FA'
    | 'FORGET_PASSWORD';
  isLoading: boolean;
};

const initialState = {
  email: '',
  password: '',
  cognitoUser: {},
  token2fa: '',
  logged: false,
  step: 'LOGIN',
  isLoading: false,
};

const Context = createContext<{ state: State; dispatch: Dispatch } | undefined>(
  undefined
);

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case 'SET_EMAIL': {
      return { ...state, email: action.payload };
    }
    case 'SET_PASSWORD': {
      return { ...state, password: action.payload };
    }
    case 'SET_STEP': {
      return { ...state, step: action.payload };
    }
    case 'SET_COGNITO_USER': {
      return { ...state, cognitoUser: action.payload };
    }
    case 'SET_2FA_TOKEN': {
      return { ...state, token2fa: action.payload };
    }
    case 'SET_LOGGED': {
      return { ...state, logged: action.payload };
    }
    case 'SET_LOADING': {
      return { ...state, isLoading: action.payload };
    }
    case 'CLEAR': {
      return { ...initialState, ...action.payload };
    }
    default: {
      return state;
    }
  }
};

export const SignInProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(reducer, { ...initialState });

  const value = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useSignInContext = () => {
  const context = useContext(Context);
  if (context === undefined) {
    throw new Error('useSignInContext must be used within a SignInProvider');
  }

  return context;
};

export const SignInPage: React.FC = () => (
  <SignInProvider>
    <SignIn />
  </SignInProvider>
);
